import React, { useState } from 'react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import * as XLSX from 'xlsx';
import Navbar from '../../components/navbar/navbar.jsx'
import apinode from '../../services/apinode.js';

// pdfMake.vfs = pdfFonts.pdfMake.vfs;

const SalesDashboard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLoja, setSelectedLoja] = useState('all');
  const [sortBy, setSortBy] = useState('faturamento');
  const hoje = new Date().toISOString().split('T')[0];

  const [dataInicio, setDataInicio] = useState(hoje);
  const [dataFim, setDataFim] = useState(hoje);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Buscar dados da API
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data1 = dataInicio.split('-').reverse().join('/');
      const data2 = dataFim.split('-').reverse().join('/');

      const response = await apinode.get(
        `/relprodutos/relatorio/vendas/${localStorage.idconta}?data1=${data1}&data2=${data2}`
      );

      if (!response.data) {
        throw new Error('Erro ao buscar dados');
      }

      const result = await response.data;
      setData(result.data || []);
      setCurrentPage(1);
    } catch (err) {
      setError('Erro ao carregar dados da API.');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);

  const getLojaStats = () => {
    const stats = {};
    data.forEach((item) => {
      if (!stats[item.id_loja]) {
        stats[item.id_loja] = { total_venda: 0, total_custo: 0, lucro_total: 0, quantidade: 0, produtos: 0 };
      }
      stats[item.id_loja].total_venda += item.total_venda;
      stats[item.id_loja].total_custo += item.total_custo;
      stats[item.id_loja].lucro_total += item.lucro_total;
      stats[item.id_loja].quantidade += item.total_quantidade;
      stats[item.id_loja].produtos += 1;
    });
    return stats;
  };

  const getFilteredProducts = () => {
    let filtered = selectedLoja === 'all'
      ? data
      : data.filter((item) => item.id_loja === selectedLoja);

    // Aplicar ordenação
    switch(sortBy) {
      case 'faturamento':
        return filtered.sort((a, b) => b.total_venda - a.total_venda);
      case 'quantidade':
        return filtered.sort((a, b) => b.total_quantidade - a.total_quantidade);
      case 'lucro':
        return filtered.sort((a, b) => b.lucro_total - a.lucro_total);
      case 'margem':
        return filtered.sort((a, b) => {
          const margemA = a.total_venda > 0 ? (a.lucro_total / a.total_venda) * 100 : 0;
          const margemB = b.total_venda > 0 ? (b.lucro_total / b.total_venda) * 100 : 0;
          return margemB - margemA;
        });
      case 'produto':
        return filtered.sort((a, b) => a.descricao.localeCompare(b.descricao));
      case 'sku':
        return filtered.sort((a, b) => (a.sku || '').localeCompare(b.sku || ''));
      default:
        return filtered.sort((a, b) => b.total_venda - a.total_venda);
    }
  };

  const getTotalStats = () => {
    const filtered = selectedLoja === 'all'
      ? data
      : data.filter((item) => item.id_loja === selectedLoja);

    return filtered.reduce(
      (acc, item) => ({
        total_venda: acc.total_venda + item.total_venda,
        total_custo: acc.total_custo + item.total_custo,
        lucro_total: acc.lucro_total + item.lucro_total,
        quantidade: acc.quantidade + item.total_quantidade
      }),
      { total_venda: 0, total_custo: 0, lucro_total: 0, quantidade: 0 }
    );
  };

  // Exportar para Excel
  const exportarExcel = () => {
    try {
      const filteredProducts = getFilteredProducts();
      const totalStats = getTotalStats();
      const margemLucro = totalStats.total_venda > 0
        ? ((totalStats.lucro_total / totalStats.total_venda) * 100).toFixed(1)
        : 0;

      // Dados para o Excel
      const excelData = filteredProducts.map((prod, idx) => {
        const margem = prod.total_venda > 0
          ? ((prod.lucro_total / prod.total_venda) * 100).toFixed(1)
          : 0;
        
        return {
          '#': idx + 1,
          'SKU': prod.sku || 'N/A',
          'Produto': prod.descricao,
          'Loja': `Loja ${prod.id_loja}`,
          'Quantidade': prod.total_quantidade,
          'Preço Custo': prod.precocusto,
          'Faturamento': prod.total_venda,
          'Custo Total': prod.total_custo,
          'Lucro': prod.lucro_total,
          'Margem %': `${margem}%`
        };
      });

      // Adicionar linha de totais
      excelData.push({
        '#': '',
        'SKU': '',
        'Produto': 'TOTAL',
        'Loja': '',
        'Quantidade': totalStats.quantidade,
        'Preço Custo': '',
        'Faturamento': totalStats.total_venda,
        'Custo Total': totalStats.total_custo,
        'Lucro': totalStats.lucro_total,
        'Margem %': `${margemLucro}%`
      });

      // Criar workbook
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Vendas');

      // Gerar arquivo
      const fileName = `relatorio_vendas_${dataInicio}_a_${dataFim}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      setError('Erro ao exportar Excel: ' + err.message);
    }
  };

  // Gerar PDF
  const gerarPDF = () => {
    try {
      const filteredProducts = getFilteredProducts();
      const totalStats = getTotalStats();
      const margemLucro = totalStats.total_venda > 0
        ? ((totalStats.lucro_total / totalStats.total_venda) * 100).toFixed(1)
        : 0;

      const tableBody = [
        [
          { text: '#', bold: true, color: '#fff', fillColor: '#0d6efd' },
          { text: 'SKU', bold: true, color: '#fff', fillColor: '#0d6efd' },
          { text: 'Produto', bold: true, color: '#fff', fillColor: '#0d6efd' },
          { text: 'Loja', bold: true, color: '#fff', fillColor: '#0d6efd' },
          { text: 'Qtd', bold: true, color: '#fff', fillColor: '#0d6efd' },
          { text: 'Preço Custo', bold: true, color: '#fff', fillColor: '#0d6efd' },
          { text: 'Faturamento', bold: true, color: '#fff', fillColor: '#0d6efd' },
          { text: 'Custo Total', bold: true, color: '#fff', fillColor: '#0d6efd' },
          { text: 'Lucro', bold: true, color: '#fff', fillColor: '#0d6efd' },
          { text: 'Margem %', bold: true, color: '#fff', fillColor: '#0d6efd' }
        ]
      ];

      filteredProducts.forEach((prod, idx) => {
        const margem = prod.total_venda > 0
          ? ((prod.lucro_total / prod.total_venda) * 100).toFixed(1)
          : 0;

        const bgColor = idx % 2 === 0 ? '#f8f9fa' : '#fff';

        tableBody.push([
          { text: String(idx + 1), fillColor: bgColor },
          { text: prod.sku || 'N/A', fillColor: bgColor, fontSize: 8 },
          { text: prod.descricao.substring(0, 35), fillColor: bgColor },
          { text: `Loja ${prod.id_loja}`, alignment: 'center', fillColor: bgColor },
          { text: String(prod.total_quantidade), alignment: 'center', fillColor: bgColor },
          { text: formatCurrency(prod.precocusto), alignment: 'right', fillColor: bgColor },
          { text: formatCurrency(prod.total_venda), alignment: 'right', color: '#198754', bold: true, fillColor: bgColor },
          { text: formatCurrency(prod.total_custo), alignment: 'right', fillColor: bgColor },
          { text: formatCurrency(prod.lucro_total), alignment: 'right', color: '#0d6efd', bold: true, fillColor: bgColor },
          { text: `${margem}%`, alignment: 'right', fillColor: bgColor }
        ]);
      });

      const docDefinition = {
        pageOrientation: 'landscape',
        pageSize: 'A4',
        pageMargins: [40, 50, 40, 60],
        header: {
          text: 'RELATÓRIO DE VENDAS',
          fontSize: 18,
          bold: true,
          color: '#0d6efd',
          alignment: 'center',
          margin: [40, 20, 40, 5]
        },
        footer: function(currentPage, pageCount) {
          return {
            text: `Página ${currentPage} de ${pageCount}`,
            alignment: 'center',
            fontSize: 9,
            color: '#666',
            margin: [40, 20, 40, 20]
          };
        },
        content: [
          {
            text: `Período: ${dataInicio} a ${dataFim}`,
            fontSize: 11,
            color: '#666',
            alignment: 'center',
            marginBottom: 20
          },

          {
            columns: [
              {
                stack: [
                  { text: 'FATURAMENTO', fontSize: 9, color: '#fff', bold: true },
                  { text: formatCurrency(totalStats.total_venda), fontSize: 14, bold: true, color: '#fff', marginTop: 5 }
                ],
                fillColor: '#198754',
                padding: [12, 15, 12, 15],
                margin: [0, 0, 10, 0]
              },
              {
                stack: [
                  { text: 'LUCRO', fontSize: 9, color: '#fff', bold: true },
                  { text: formatCurrency(totalStats.lucro_total), fontSize: 14, bold: true, color: '#fff', marginTop: 5 }
                ],
                fillColor: '#0d6efd',
                padding: [12, 15, 12, 15],
                margin: [0, 0, 10, 0]
              },
              {
                stack: [
                  { text: 'PRODUTOS', fontSize: 9, color: '#fff', bold: true },
                  { text: String(totalStats.quantidade), fontSize: 14, bold: true, color: '#fff', marginTop: 5 }
                ],
                fillColor: '#6c757d',
                padding: [12, 15, 12, 15],
                margin: [0, 0, 10, 0]
              },
              {
                stack: [
                  { text: 'MARGEM', fontSize: 9, color: '#000', bold: true },
                  { text: `${margemLucro}%`, fontSize: 14, bold: true, color: '#000', marginTop: 5 }
                ],
                fillColor: '#ffc107',
                padding: [12, 15, 12, 15]
              }
            ],
            marginBottom: 25
          },

          {
            text: `LISTA DE PRODUTOS (${filteredProducts.length})`,
            fontSize: 12,
            bold: true,
            color: '#333',
            marginBottom: 10
          },

          {
            table: {
              headerRows: 1,
              widths: ['4%', '8%', '24%', '7%', '6%', '10%', '11%', '10%', '11%', '9%'],
              body: tableBody
            },
            layout: {
              hLineWidth: function(i, node) {
                if (i === 0 || i === node.table.body.length) {
                  return 2;
                }
                return 0.5;
              },
              vLineWidth: function() {
                return 0.5;
              },
              hLineColor: '#dee2e6',
              vLineColor: '#dee2e6',
              paddingLeft: function() { return 8; },
              paddingRight: function() { return 8; },
              paddingTop: function() { return 6; },
              paddingBottom: function() { return 6; }
            },
            fontSize: 9
          },

          {
            marginTop: 25,
            columns: [
              { text: `Gerado em: ${new Date().toLocaleString('pt-BR')}`, fontSize: 8, color: '#999' },
              { text: `Loja: ${selectedLoja === 'all' ? 'Todas' : `Loja ${selectedLoja}`}`, fontSize: 8, color: '#999', alignment: 'right' }
            ]
          }
        ]
      };

      pdfMake.createPdf(docDefinition).open();
    } catch (err) {
      setError('Erro ao gerar PDF: ' + err.message);
    }
  };

  const lojaStats = getLojaStats();
  const filteredProducts = getFilteredProducts();
  const totalStats = getTotalStats();
  const margemLucro = totalStats.total_venda > 0
    ? ((totalStats.lucro_total / totalStats.total_venda) * 100).toFixed(1)
    : 0;

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIdx, startIdx + itemsPerPage);

  return (
    <>
    <Navbar/>
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />
      <div className="min-vh-100 bg-light">
        {/* Header */}
        <nav className="navbar navbar-expand-lg navbar-light bg-white border-bottom sticky-top shadow-sm">
          <div className="container-lg">
            <span className="navbar-brand mb-0 h1 fw-bold text-primary">
              📊 Vendas Pro
            </span>
            {data.length > 0 && (
              <>
                <button
                  onClick={gerarPDF}
                  className="btn btn-warning fw-semibold me-2"
                >
                  📥 Gerar PDF
                </button>
                <button
                  onClick={exportarExcel}
                  className="btn btn-success fw-semibold"
                >
                  📊 Exportar Excel
                </button>
              </>
            )}
          </div>
        </nav>

        <div className="container-lg py-4">
          {/* Filtros */}
          <div className="card shadow-sm mb-4 border-0">
            <div className="card-body">
              <h6 className="card-title fw-bold mb-3">Filtros de Busca</h6>
              <div className="row g-3">
                <div className="col-lg-3">
                  <label className="form-label fw-semibold">Data Início</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                  />
                </div>
                <div className="col-lg-3">
                  <label className="form-label fw-semibold">Data Fim</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                  />
                </div>
                <div className="col-lg-3">
                  <label className="form-label fw-semibold">Loja</label>
                  <select
                    className="form-select"
                    value={selectedLoja}
                    onChange={(e) => setSelectedLoja(e.target.value)}
                  >
                    <option value="all">Todas as Lojas</option>
                    {Object.keys(lojaStats).map((loja) => (
                      <option key={loja} value={loja}>
                        Loja {loja}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-2">
                  <label className="form-label fw-semibold">Ordenar por</label>
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="faturamento">💰 Faturamento</option>
                    <option value="quantidade">📦 Quantidade</option>
                    <option value="lucro">💵 Lucro</option>
                    <option value="margem">📊 Margem %</option>
                    <option value="produto">🔤 Produto (A-Z)</option>
                    <option value="sku">🏷️ SKU</option>
                  </select>
                </div>
                <div className="col-lg-1">
                  <label className="form-label fw-semibold">&nbsp;</label>
                  <button
                    onClick={fetchData}
                    className="btn btn-primary w-100 fw-semibold"
                    disabled={loading}
                  >
                    {loading ? '⏳' : '🔍'}
                  </button>
                </div>
              </div>
              {error && (
                <div className="alert alert-danger mt-3 mb-0" role="alert">
                  {error}
                </div>
              )}
            </div>
          </div>

          {/* Estatísticas */}
          {data.length > 0 && (
            <>
              <div className="row g-3 mb-4">
                <div className="col-lg-3">
                  <div className="card bg-success text-white shadow-sm border-0 h-100">
                    <div className="card-body">
                      <small className="fw-semibold opacity-75">Faturamento</small>
                      <h4 className="mt-2 mb-0">{formatCurrency(totalStats.total_venda)}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="card bg-primary text-white shadow-sm border-0 h-100">
                    <div className="card-body">
                      <small className="fw-semibold opacity-75">Lucro Total</small>
                      <h4 className="mt-2 mb-0">{formatCurrency(totalStats.lucro_total)}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="card bg-info text-white shadow-sm border-0 h-100">
                    <div className="card-body">
                      <small className="fw-semibold opacity-75">Produtos</small>
                      <h4 className="mt-2 mb-0">{totalStats.quantidade}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="card bg-warning text-dark shadow-sm border-0 h-100">
                    <div className="card-body">
                      <small className="fw-semibold opacity-75">Margem Lucro</small>
                      <h4 className="mt-2 mb-0">{margemLucro}%</h4>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabela */}
              <div className="card shadow-sm border-0">
                <div className="card-header bg-primary text-white fw-bold">
                  📦 Lista de Produtos ({filteredProducts.length})
                </div>
                <div className="table-responsive">
                  <table className="table table-hover table-striped mb-0 table-sm">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '4%', minWidth: '40px' }}>#</th>
                        <th style={{ width: '8%', minWidth: '80px' }}>SKU</th>
                        <th style={{ minWidth: '280px' }}>Produto</th>
                        <th style={{ width: '7%', minWidth: '70px' }} className="text-center">Loja</th>
                        <th style={{ width: '6%', minWidth: '50px' }} className="text-center">Qtd</th>
                        <th style={{ width: '10%', minWidth: '100px' }} className="text-end">Preço Custo</th>
                        <th style={{ width: '11%', minWidth: '110px' }} className="text-end">Faturamento</th>
                        <th style={{ width: '10%', minWidth: '100px' }} className="text-end">Custo Total</th>
                        <th style={{ width: '11%', minWidth: '100px' }} className="text-end">Lucro</th>
                        <th style={{ width: '9%', minWidth: '80px' }} className="text-end">Margem %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedProducts.map((prod, idx) => {
                        const margem = prod.total_venda > 0
                          ? ((prod.lucro_total / prod.total_venda) * 100).toFixed(1)
                          : 0;
                        return (
                          <tr key={startIdx + idx}>
                            <td style={{ fontSize: '0.9rem' }}>{startIdx + idx + 1}</td>
                            <td style={{ fontSize: '0.85rem' }} className="text-muted">{prod.sku || 'N/A'}</td>
                            <td className="fw-semibold" style={{ fontSize: '0.9rem' }}>{prod.descricao.substring(0, 45)}</td>
                            <td className="text-center" style={{ fontSize: '0.9rem' }}>Loja {prod.id_loja}</td>
                            <td className="text-center" style={{ fontSize: '0.9rem' }}>{prod.total_quantidade}</td>
                            <td className="text-end" style={{ fontSize: '0.9rem' }}>{formatCurrency(prod.precocusto)}</td>
                            <td className="text-end text-success fw-semibold" style={{ fontSize: '0.9rem' }}>{formatCurrency(prod.total_venda)}</td>
                            <td className="text-end text-danger" style={{ fontSize: '0.9rem' }}>{formatCurrency(prod.total_custo)}</td>
                            <td className="text-end text-primary fw-semibold" style={{ fontSize: '0.9rem' }}>{formatCurrency(prod.lucro_total)}</td>
                            <td className="text-end" style={{ fontSize: '0.9rem' }}>{margem}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="card-footer bg-light d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
                    </small>
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        ◀ Anterior
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Próximo ▶
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {data.length === 0 && !loading && (
            <div className="card border-0 shadow-sm text-center py-5">
              <div className="card-body">
                <p className="text-muted fs-5">🔍 Clique em "Buscar" para carregar os dados de vendas</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export { SalesDashboard };